import {useState} from "react";
import Breadcrumbs from "./Breadcrumbs";
import CustomerSelect from "./CustomerSelect";
import PatientSelect from "./PatientSelect";
import EvalGraphs from "./EvalGraphs";
import { PhotoIcon, UserCircleIcon } from '@heroicons/react/24/solid'

import { ref, getDatabase } from 'firebase/database';
import { useObjectVal } from "react-firebase-hooks/database";
import SelectOrganization from "../shared/select-organization";

const clientGenders = [
    { id: 'Male', text: 'Male'},
    { id: 'Female', text: 'Female'}
];

const notificationMethods = [
    { id: 'belowNormal', title: 'Below Normal' },
    { id: 'belowNormalElevation', title: 'Below Normal with Elevation' },
    { id: 'normal', title: 'Normal' },
    { id: 'aboveNormal', title: 'Above Normal' },
    { id: 'significantlyAboveNormal', title: 'Significantly Above Normal' },
]

function VisualFixationGroup({tannerScale}) {
    return (
        <div>
            <label className="text-base font-semibold text-gray-900">Visual Fixation</label>
            <p className="text-sm text-gray-500">{tannerScale}</p>
            <fieldset className="mt-4">
                <legend className="sr-only">Visual Fixation</legend>
                <div className="space-y-4">
                    {notificationMethods.map((notificationMethod) => (
                        <div key={notificationMethod.id + tannerScale} className="flex items-center">
                            <input
                                id={notificationMethod.id + tannerScale}
                                name="notification-method"
                                type="radio"
                                defaultChecked={notificationMethod.id === 'belowNormal'}
                                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                            <label htmlFor={notificationMethod.id + tannerScale} className="ml-3 block text-sm font-medium leading-6 text-gray-900">
                                {notificationMethod.title}
                            </label>
                        </div>
                    ))}
                </div>
            </fieldset>
        </div>
    )
}

function GenderSelect({setGender}) {
    return (
        <div className="py-10">
            <label className="text-base font-semibold text-gray-900">Gender</label>
            <p className="text-sm text-gray-500">Please select patient gender</p>
            <fieldset className="mt-4">
                <legend className="sr-only">Notification method</legend>
                <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                    {clientGenders.map((clientGender) => (
                        <div key={clientGender.id} className="flex items-center">
                            <input
                                id={clientGender.id}
                                name="notification-method"
                                type="radio"
                                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                onClick={() => setGender(clientGender.id)}
                            />
                            <label htmlFor={clientGender.id} className="ml-3 block text-sm font-medium leading-6 text-gray-900">
                                {clientGender.text}
                            </label>
                        </div>
                    ))}
                </div>
            </fieldset>
        </div>
    );
}

function EvalForm() {
    return (
        <form className="py-10 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <div className="px-4 py-6 sm:p-8">
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-full">
                    <VisualFixationGroup tannerScale={"Adult Female"}/>
                </div>

                <div className="col-span-full">
                    <VisualFixationGroup tannerScale={"Adult Male"}/>
                </div>

                <div className="col-span-full">
                    <label htmlFor="cover-photo" className="block text-sm font-medium leading-6 text-gray-900">
                        Cover photo
                    </label>
                    <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                        <div className="text-center">
                            <PhotoIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                            <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                                >
                                    <span>Upload a file</span>
                                    <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs leading-5 text-gray-600">PNG, JPG, GIF up to 10MB</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button type="button" className="text-sm font-semibold leading-6 text-gray-900">
                Cancel
            </button>
            <button
                type="submit"
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
                Save
            </button>
        </div>
    </form>
    );
}

/**
 * View an evaluation
 **/
export default function ViewEval({user}) {
    const [activeCustomer, setActiveCustomer] = useState(null);
    const [gender, setGender] = useState("");
    const [selectedCase, setSelectedCase] = useState(null);
    const [patientCache, setPatientCache] = useState({});
    const [selectedEvalId, setSelectedEval] = useState("");
    const [customers, loading, error] = useObjectVal(ref(getDatabase(), 'customers'));

    function patientsToCache(key, value) {
        let update = {};
        update[key] = value;
        setPatientCache({...patientCache, ...update});
    }

    let toShow = null;
    if (loading) {
        return <p>Loading...</p>
    }
    if (activeCustomer === null) {
        // toShow = <SelectOrganization organizations={customers} setOrganization={setActiveCustomer} >
        toShow = <CustomerSelect customers={customers} setActiveCustomer={setActiveCustomer} />
    } else if (gender === "") {
        toShow = <GenderSelect setGender={setGender} />
    } else if (selectedCase === null) {
        toShow = <PatientSelect setSelectedCase={setSelectedCase}
                                setSelectedEval={setSelectedEval}
                                gender={gender} activeCustomer={activeCustomer}
                                patientsToCache={patientsToCache}
                                patientCache={patientCache} />
    } else {
        toShow = (<EvalGraphs selectedCase={selectedCase} selectedEvalId={selectedEvalId} user={user} />);
    }

    return (
        <div>
            <div>
                <Breadcrumbs activeCustomer={activeCustomer !== null ? customers[activeCustomer] : null}
                             setActiveCustomer={setActiveCustomer}
                             gender={gender}
                             setGender={setGender}
                             selectedCase={selectedCase}
                             setSelectedCase={setSelectedCase} />
                {toShow}
            </div>
        </div>
    )
}
